import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDocument } from 'hooks/api/documents/useDocument';
import {
    Typography,
    IconButton,
    Box,
    Paper,
    Grid,
    Button,
    Dialog,
    DialogTitle as MuiDialogTitle,
    DialogContent as MuiDialogContent,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    Check,
    Clear,
    Person as PersonIcon,
    LocationOn as LocationIcon,
    Business as BusinessIcon,
    CalendarMonth as CalendarIcon,
    AccountBalance as AccountBalanceIcon,
    Email as EmailIcon,
    Badge as BadgeIcon,
    Article as ArticleIcon,
    AccessTime as AccessTimeIcon,
    InsertDriveFile as InsertDriveFileIcon,
    Image as ImageIcon,
    Description as DescriptionIcon,
    TableChart as TableChartIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { theme } from '../../ConstantColors';
import { RejectionDialog } from './rejectionReasonDialog';
import TableCollapsible from 'views/trip/daily-exp/DailyExpensesCollapsibleTable';
import MainCard from 'ui-component/cards/MainCard';
import { useTravelRequest } from 'hooks/api/travel-request/useTravelRequest';
import { InfoSection } from 'ui-component/commons/InfoSection';
import { LabelValue } from 'ui-component/commons/LabelValue';
import { formatCurrency, formatDate } from 'utils/formatters';
import { DocumentSection } from 'views/documents/DocumentSection';
import { useKeycloak } from '@react-keycloak/web';

// Reusable styled components

const TripPreview = () => {
    const { requestId } = useParams();
    const navigate = useNavigate();
    const { getRequestById, getLineActivities, selectedRequest, lineActivities, loading, bulkUpdateRequests } = useTravelRequest();
    const [error, setError] = useState(null);
    const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const { documents, fetchDocuments, downloadDocument, previewDocument } = useDocument();
    const { keycloak } = useKeycloak();

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                await getRequestById(requestId);
                await getLineActivities(requestId);
            } catch (err) {
                console.error('Error fetching trip details:', err);
                setError('Failed to load trip details');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [requestId, getRequestById, getLineActivities]);

    useEffect(() => {
        if (selectedRequest?.latestAcquittalId) {
            fetchDocuments(selectedRequest.latestAcquittalId);
        }
    }, [selectedRequest?.latestAcquittalId, fetchDocuments]);

    const getFileDetails = (fileName) => {
        const extension = fileName?.toLowerCase().split('.').pop();
        let icon = <ArticleIcon />;
        let canPreview = false;
        let contentType = 'application/octet-stream';

        switch (extension) {
            case 'pdf':
                icon = <ArticleIcon />;
                canPreview = true;
                contentType = 'application/pdf';
                break;
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
                icon = <ImageIcon />;
                canPreview = true;
                contentType = `image/${extension}`;
                break;
            case 'doc':
            case 'docx':
                icon = <DescriptionIcon />;
                contentType = 'application/msword';
                break;
            case 'xls':
            case 'xlsx':
                icon = <TableChartIcon />;
                contentType = 'application/vnd.ms-excel';
                break;
            default:
                icon = <InsertDriveFileIcon />;
        }

        return { icon, canPreview, extension, contentType };
    };

    const handleDocumentPreview = async (documentId, fileName) => {
        const { canPreview } = getFileDetails(fileName);

        if (!canPreview) {
            downloadDocument(documentId, fileName);
            return;
        }

        try {
            const url = await previewDocument(documentId);
            setPreviewUrl(url);
            setSelectedDocument(fileName);
            setIsPreviewOpen(true);
        } catch (error) {
            console.error('Error previewing document:', error);
        }
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setSelectedDocument(null);
    };

    const handleApprove = () => {
        const confirmApproval = async (result) => {
            const currentRole = selectedRequest.currentApprovalStage;
            if (!result.isConfirmed) return;

            try {
                const validStages = ['ROLE_SUPERVISOR', 'ROLE_FINANCE', 'ROLE_HOD'];
                const { currentApprovalStage } = selectedRequest;

                if (!validStages.includes(currentApprovalStage)) {
                    throw new Error('Invalid approval stage');
                }

                await bulkUpdateRequests({
                    travelRequestIds: [requestId],
                    currentApproverLevel: currentApprovalStage,
                    status: 'APPROVED',
                    comments: ''
                });

                await Swal.fire('Approved!', 'The travel request has been approved.', 'success');
                if (currentRole === 'ROLE_SUPERVISOR') {
                    navigate('/dashboard/trips-adminstration-s');
                }
                if (currentRole === 'ROLE_FINANCE') {
                    navigate('/dashboard/trips-adminstration-f');
                }
                if (currentRole === 'ROLE_HOD') {
                    navigate('/dashboard/trips-adminstration-h');
                }
            } catch (error) {
                console.error('Error approving request:', error);
                await Swal.fire('Error', 'Failed to approve the request', 'error');
            }
        };

        Swal.fire({
            title: 'Confirm Approval',
            text: 'Are you sure you want to approve this travel request?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: theme.primary.main,
            cancelButtonColor: theme.error.main,
            confirmButtonText: 'Yes, Approve',
            cancelButtonText: 'Cancel'
        }).then(confirmApproval);
    };

    const handleReject = (reasonText) => {
        const currentRole = selectedRequest.currentApprovalStage;
        if (typeof reasonText === 'string' && reasonText.trim()) {
            bulkUpdateRequests({
                travelRequestIds: [requestId],
                currentApproverLevel: selectedRequest.currentApprovalStage,
                status: 'DECLINED',
                comments: reasonText
            })
                .then(async () => {
                    setIsRejectionDialogOpen(false);
                    await Swal.fire('Rejected!', 'The travel request has been rejected.', 'success');
                    if (currentRole === 'ROLE_SUPERVISOR') {
                        navigate('/dashboard/trips-adminstration-s');
                    }
                    if (currentRole === 'ROLE_FINANCE') {
                        navigate('/dashboard/trips-adminstration-f');
                    }
                    if (currentRole === 'ROLE_HOD') {
                        navigate('/dashboard/trips-adminstration-h');
                    }
                })
                .catch((error) => {
                    console.error('Error rejecting request:', error);
                    Swal.fire('Error', 'Failed to reject the request', 'error');
                });
        }
    };

    if (loading || isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!selectedRequest) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography>Trip not found</Typography>
            </Box>
        );
    }

    return (
        <MainCard>
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                    <Typography variant="h3" sx={{ fontWeight: 600 }}>
                        Travel Request - {selectedRequest.sequenceNumber} - {selectedRequest.travelPlan?.planName}
                    </Typography>
                </Box>

                {/* Request Overview */}
                <InfoSection title="Request Details" icon={ArticleIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Reference Number" value={selectedRequest.sequenceNumber} chip />
                            <LabelValue label="Status" value={selectedRequest.status} chip />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Created At" value={formatDate(selectedRequest.createdAt)} icon={AccessTimeIcon} />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Requester Information */}
                <InfoSection title="Requester Information" icon={PersonIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue
                                label="Full Name"
                                value={`${selectedRequest.requestUser?.firstName} ${selectedRequest.requestUser?.lastName}`}
                                icon={PersonIcon}
                            />
                            <LabelValue label="Email Address" value={selectedRequest.requestUser?.email} icon={EmailIcon} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Employee Number" value={selectedRequest.requestUser?.employeeNumber} icon={BadgeIcon} />
                            <LabelValue label="Status" value={selectedRequest.requestUser?.status} chip />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Traveller Information */}
                <InfoSection title="Traveller Information" icon={PersonIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue
                                label="Full Name"
                                value={`${selectedRequest.user?.firstName} ${selectedRequest.user?.lastName}`}
                                icon={PersonIcon}
                            />
                            <LabelValue label="Email Address" value={selectedRequest.user?.email} icon={EmailIcon} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Employee Number" value={selectedRequest.user?.employeeNumber} icon={BadgeIcon} />
                            <LabelValue label="Status" value={selectedRequest.user?.status} chip />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Travel Plan Details */}
                <InfoSection title="Travel Plan Details" icon={CalendarIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Trip Purpose" value={selectedRequest.travelPlan?.planName} icon={ArticleIcon} />
                            <LabelValue label="Travel Type" value={selectedRequest.travelPlan?.type?.replace(/_/g, ' ')} chip />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Venue" value={selectedRequest.travelPlan?.site?.name} chip />
                            <LabelValue
                                label="Location"
                                value={`${selectedRequest.travelPlan?.site?.district?.name}, ${selectedRequest.travelPlan?.site?.district?.province?.name}`}
                                icon={LocationIcon}
                            />
                        </Grid>

                        {/* Documents section */}
                        <Grid item xs={12}>
                            <DocumentSection documents={documents} theme={theme} keycloak={keycloak} />{' '}
                        </Grid>

                        <Grid item xs={12}>
                            <LabelValue
                                label="Travel Duration"
                                value={`From: ${formatDate(selectedRequest.departureDatetime)} To: ${formatDate(
                                    selectedRequest.returnDatetime
                                )}`}
                                icon={AccessTimeIcon}
                            />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Budget Information */}
                <InfoSection title="Budget Information" icon={AccountBalanceIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Budget Line" value={selectedRequest.budgetLine?.code} chip />
                            <LabelValue
                                label="Branch Code"
                                value={`${selectedRequest.branchCode?.code} - ${selectedRequest.branchCode?.description}`}
                                icon={BusinessIcon}
                            />
                            <LabelValue
                                label="Project Code"
                                value={`${selectedRequest.budgetLine?.projectCode?.code} - ${selectedRequest.budgetLine?.projectCode?.description}`}
                                icon={BusinessIcon}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Budget Amount" value={formatCurrency(selectedRequest.budgetAmount)} chip />
                            <LabelValue label="Fiscal Year" value={selectedRequest.budgetLine?.fiscalYear} icon={CalendarIcon} />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Daily Activities */}
                <TableCollapsible lineActivities={lineActivities} />

                {/* Action Buttons */}
                {selectedRequest.status === 'PENDING' && (
                    <Paper
                        elevation={0}
                        sx={{
                            mt: 4,
                            p: 3,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 2,
                            borderRadius: 2,
                            bgcolor: theme.background.light,
                            borderTop: `1px solid ${theme.primary.light}`
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={() => setIsRejectionDialogOpen(true)}
                            sx={{
                                bgcolor: theme.error.main,
                                '&:hover': { bgcolor: theme.error.dark }
                            }}
                            startIcon={<Clear />}
                        >
                            Reject Request
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleApprove}
                            sx={{
                                bgcolor: theme.primary.main,
                                '&:hover': { bgcolor: theme.primary.dark }
                            }}
                            startIcon={<Check />}
                        >
                            Approve Request
                        </Button>
                    </Paper>
                )}
            </Box>

            {/* Document Preview Dialog */}
            <Dialog
                open={isPreviewOpen}
                onClose={handleClosePreview}
                maxWidth="lg"
                fullWidth
                sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
            >
                <MuiDialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: theme.primary.main,
                        color: 'white'
                    }}
                >
                    <Typography variant="h6">{selectedDocument || 'Document Preview'}</Typography>
                    <IconButton onClick={handleClosePreview} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </MuiDialogTitle>
                <MuiDialogContent sx={{ p: 0, height: '100%' }}>
                    {previewUrl &&
                        (getFileDetails(selectedDocument).extension === 'pdf' ? (
                            <iframe src={previewUrl} title="PDF Preview" width="100%" height="100%" style={{ border: 'none' }} />
                        ) : (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    bgcolor: 'black'
                                }}
                            >
                                <img
                                    src={previewUrl}
                                    alt={selectedDocument}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        ))}
                </MuiDialogContent>
            </Dialog>

            {/* Rejection Dialog */}
            <RejectionDialog
                open={isRejectionDialogOpen}
                onClose={() => setIsRejectionDialogOpen(false)}
                onReject={handleReject}
                requestNumber={selectedRequest?.sequenceNumber}
            />
        </MainCard>
    );
};

InfoSection.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    children: PropTypes.node.isRequired,
    elevation: PropTypes.number,
    sx: PropTypes.object
};

LabelValue.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    icon: PropTypes.elementType,
    chip: PropTypes.bool
};

export default TripPreview;
